import * as Yup from 'yup';
import { Field, Formik, FormikErrors } from 'formik';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { useApi } from '../../context';
import { DataRoomCall, ActiveDataRoomUser } from '../../models';
import { FormikUtils } from '../../utils';
import { CerebroDrawer } from '../CerebroDrawer';
import { CerebroHeading } from '../CerebroHeading';
import { getApiResponseData } from '../../../shared/utils/axios';
import { CerebroForm } from '../CerebroForm';
import { Grid } from '@mui/material';
import { CerebroLabel } from '../CerebroLabel';
import { CerebroText } from '../CerebroText';
import { CerebroPrimaryButton } from '../CerebroPrimaryButton';
import { CerebroTextarea } from '../CerebroTextarea';
import { CerebroApiEndpoints } from '../../utils/api';
import { QueryKeys } from '../../hooks';
import { EmptyGuid } from '../../constants';

interface LenderRequestCallFormProps {
	dataRoomGuid: string | undefined;
	activeUser: ActiveDataRoomUser | null | undefined;
	close: () => void;
}

export const LenderRequestCallForm: React.FC<LenderRequestCallFormProps> = ({ dataRoomGuid, activeUser, close }) => {
	const api = useApi();
	const client = useQueryClient();
	const snackbar = useSnackbar();

	const addCallMutation = useMutation(
		async ({ values }: LenderRequestCallFormMutationVariables) => {
			const patchValues = {
				bankDealProfileGuid: EmptyGuid,
				callDate: null,
				callDetails: values.callDetails,
				callTimeZone: '',
				callStatus: 'Proposed',
				sendEmail: true,
			};

			const { data, error } = await getApiResponseData<DataRoomCall>(
				api.post(CerebroApiEndpoints.Calls.Root(dataRoomGuid), patchValues)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.Calls.Lender(dataRoomGuid, activeUser?.bankDealProfileGuid));
				snackbar.enqueueSnackbar('Call with borrower was successfully proposed.', { variant: 'success' });
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={activeUser != null} onClose={close}>
			<CerebroHeading size="h1" gutterBottom>
				Request Call with Borrower
			</CerebroHeading>
			<CerebroHeading size="h3" gutterBottom>
				{activeUser?.fullName} ({activeUser?.bankName})
			</CerebroHeading>

			<Formik
				validationSchema={AddCallSchema}
				initialValues={{
					callDetails: '',
				}}
				onSubmit={FormikUtils.MutateOnSubmit(addCallMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values }) => {
					return (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={8}>
									<CerebroLabel id="name">Call Details</CerebroLabel>
									<Field
										id="callDetails"
										as={CerebroTextarea}
										name="callDetails"
										rows={10}
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('callDetails', errors, touched)}
									/>
									<CerebroText variant="hint">
										Enter the best times and dates you would like to have a call with the borrower.
									</CerebroText>
									<CerebroText variant="hint">
										After submitting, your request will be shared with the Cerebro transaction
										manager who will coordinate scheduling with the borrower.
									</CerebroText>
									{FormikUtils.IsInvalid('callDetails', errors, touched) && (
										<CerebroText variant="error">{errors.callDetails}</CerebroText>
									)}
								</Grid>

								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type LenderRequestCallFormMutationVariables = FormikUtils.MutateOnSubmitVariables<LenderRequestCallFormValues>;
interface LenderRequestCallFormValues {
	callDetails: string;
}
type LenderRequestCallFormValueErrors = FormikErrors<LenderRequestCallFormValues>;

const AddCallSchema = Yup.object().shape({
	callDetails: Yup.string(),
});
